/* eslint-disable react-refresh/only-export-components -- context + hook co-located with provider */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { keycloak } from '@/lib/keycloak';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  roles: string[];
}

interface AuthContextValue {
  user: AuthUser;
  logout: () => void;
  token: string;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function deriveUser(): AuthUser {
  const parsed = keycloak.tokenParsed!;
  return {
    id: parsed.sub!,
    email: (parsed as Record<string, unknown>).email as string ?? '',
    name: (parsed as Record<string, unknown>).preferred_username as string ?? (parsed as Record<string, unknown>).name as string ?? '',
    tenantId: (parsed as Record<string, unknown>).tenant_id as string,
    roles: (parsed as Record<string, unknown>).realm_access
      ? ((parsed as Record<string, unknown>).realm_access as { roles: string[] }).roles
      : [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // lib/keycloak.ts refreshes keycloak.token on a background timer
  // (updateToken(30) every 10s) but that mutates the Keycloak instance
  // directly — nothing tells React to re-render. Every consumer of
  // useAuth().token (e.g. BrowserLiveView's WS signaling handshake) got
  // whatever token happened to be current the last time AuthProvider itself
  // re-rendered for some unrelated reason, which on a long-lived page (an
  // engagement detail view left open for hours) can be many refresh cycles
  // stale. Confirmed live: entity-setup-svc's SignalingAuthService rejected
  // the WS handshake with "jwt expired" on every reconnect attempt, using a
  // token whose 5-minute access-token TTL had long since passed, while
  // GraphQL/REST calls kept working fine because graphql_client.ts /
  // rest_client.ts / useFileUpload.ts all read `keycloak.token` directly at
  // request time instead of going through this context. Subscribing to
  // Keycloak's own refresh callback keeps `token` state in sync with what
  // keycloak-js is actually holding.
  const [token, setToken] = useState(() => keycloak.token!);
  const [user, setUser] = useState<AuthUser>(deriveUser);

  useEffect(() => {
    const syncFromKeycloak = () => {
      setToken(keycloak.token!);
      setUser(deriveUser());
    };
    keycloak.onAuthRefreshSuccess = syncFromKeycloak;
    keycloak.onAuthSuccess = syncFromKeycloak;
    return () => {
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthSuccess = undefined;
    };
  }, []);

  const value: AuthContextValue = {
    user,
    logout: () => {
      keycloak.logout();
    },
    token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
